/**
 * Company: SPHMedia
 * Description: [Portrait video card component for displaying a single video in a portrait format]
 */

import PlayIcon from "@assets/actions/play-icon.png";
import InlineVideoPlayer from "@blocks/LatestVideo/InlineVideoPlayer";
import type { LatestVideoResponse } from "@typings/Brightcove.d";
import {
  Dispatch,
  ReactElement,
  RefObject,
  SetStateAction,
  useRef,
} from "react";

type PortraitVideoCardProps = {
  video: LatestVideoResponse;
  isPlaying: boolean;
  parentRef: RefObject<HTMLDivElement | null>;
  pipContainerRef: RefObject<HTMLDivElement | null>;
  handleOnEndedVideo: () => void;
  handleOnClickThumbnail: (props: LatestVideoResponse) => void;
  isMute: boolean;
  setIsMute: Dispatch<SetStateAction<boolean>>;
  isSticky: boolean;
  setIsSticky: Dispatch<SetStateAction<boolean>>;
};

/**
 * PortraitVideoCard
 * Description: [Portrait video card component for displaying a single video in a portrait format]
 * @param props - The props required by the component (if any)
 * @returns The rendered React element
 */
export default function PortraitVideoCard({
  video,
  isPlaying,
  parentRef,
  pipContainerRef,
  handleOnEndedVideo,
  handleOnClickThumbnail,
  isMute,
  setIsMute,
  isSticky,
  setIsSticky,
}: PortraitVideoCardProps): ReactElement {
  const forwardedRef = useRef<HTMLDivElement | null>(null);
  if (isPlaying) {
    return (
      <InlineVideoPlayer
        playing={video}
        forwardedRef={forwardedRef}
        observerRef={parentRef}
        pipContainerRef={pipContainerRef}
        onEnded={handleOnEndedVideo}
        isMute={isMute}
        setIsMute={setIsMute}
        isSticky={isSticky}
        setIsSticky={setIsSticky}
      />
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => {
          handleOnClickThumbnail(video);
        }}
      >
        <div className="relative w-full">
          <div className="absolute inset-0 bg-black-100/40 z-10"></div>
          <img
            src={video.poster}
            width={230}
            height={430}
            alt="video-thumbnail"
            className="object-cover w-full h-full z-20"
            style={{
              aspectRatio: `230/430`,
            }}
          />
          <div className="absolute inset-0 flex items-center justify-center z-20">
            <img className="h-[40px]" src={PlayIcon} alt="play-white-button" />
          </div>
        </div>
      </button>
    </>
  );
}
